import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CopyButton, Icon, api } from "../lib/core";

export const ReferralProgram = ({ onOpenWallet, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState("invite");
  const [regenerating, setRegenerating] = useState(false);
  const [cashback, setCashback] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  const fetchCashback = () => api.get("/wallet/cashback").then((r) => setCashback(r.data)).catch(() => {});

  const fetchInfo = () => {
    setLoading(true);
    api.get("/referral/info")
      .then((res) => setData(res.data))
      .catch((e) => console.error("referral info failed", e))
      .finally(() => setLoading(false));
    fetchCashback();
  };
  useEffect(fetchInfo, []);

  const transferCashback = async () => {
    setTransferring(true);
    try {
      const r = await api.post("/wallet/cashback/transfer");
      toast.success(`₦${Number(r.data.transferred).toLocaleString()} moved to your wallet 🎉`);
      fetchCashback();
      if (onOpenWallet) { /* balance updates on next wallet view */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't transfer cashback");
    } finally { setTransferring(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/referral/regenerate");
      fetchInfo();
    } catch (e) {
      console.error(e);
    } finally {
      setRegenerating(false);
    }
  };

  const applyReferral = async () => {
    const code = applyCode.trim();
    if (!code) return;
    setApplying(true);
    try {
      await api.post("/wallet/apply-referral", { referral_code: code });
      toast.success("Referral code applied! Your friend will be rewarded once you fund & buy.");
      setApplyCode("");
      fetchInfo();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" && !d.trim().startsWith("<") ? d : "Couldn't apply that code");
    } finally { setApplying(false); }
  };

  const shareWhatsApp = () => {
    const msg = `Join me on TopIt and get rewarded! Use my code ${data?.referral_code} or sign up here: ${data?.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const shareLink = async () => {
    const payload = { title: "Join me on TopIt", text: `Use my referral code ${data?.referral_code}`, url: data?.referral_link };
    if (navigator.share) {
      try { await navigator.share(payload); } catch (e) { /* cancelled */ }
    } else {
      navigator.clipboard?.writeText(data?.referral_link || "");
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
      </div>
    );
  }

  // ---------- SUCCESS / REWARDS SCREEN ----------
  if (screen === "success") {
    const unlocked = data.last_unlocked_milestone;
    const target = unlocked ? unlocked.target : data.next_milestone?.target || 5;
    const bonus = unlocked ? unlocked.bonus : data.next_milestone?.bonus || 0;
    const current = unlocked ? target : data.friends_joined;
    const pct = unlocked ? 100 : Math.round(Math.min(current / target * 100, 100));
    return (
      <div className="max-w-lg mx-auto" data-testid="referral-success-page">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setScreen("invite")} data-testid="success-back-btn" className="p-2 hover:bg-slate-100 rounded-lg text-slate-700">
            <Icon name="arrow_back" />
          </button>
          <h2 className="font-bold text-xl text-slate-900 flex-1 text-center pr-9">Referral Rewards</h2>
        </div>

        {/* Hero */}
        <div className="text-center pt-4">
          <div className="relative inline-flex items-center justify-center w-40 h-40">
            <div className="absolute inset-0 bg-red-100 rounded-full blur-2xl opacity-60" />
            <Icon name="arrow_upward" className="relative text-primary-custom" style={{ fontSize: "6rem" }} />
            <Icon name="paid" className="absolute top-2 left-4 text-amber-400 text-2xl" />
            <Icon name="paid" className="absolute top-6 right-3 text-amber-400 text-xl" />
            <Icon name="paid" className="absolute bottom-4 left-2 text-amber-400 text-lg" />
            <Icon name="paid" className="absolute bottom-6 right-6 text-amber-400 text-2xl" />
          </div>
          {unlocked && (
            <div className="mt-2">
              <span className="inline-block bg-primary-custom text-white text-sm font-bold px-4 py-1.5 rounded-full" data-testid="milestone-badge">MILESTONE REACHED 🎉</span>
            </div>
          )}
          <h3 className="text-3xl font-extrabold text-slate-900 mt-4 leading-tight">
            {unlocked ? "You unlocked" : "Keep going!"}<br />
            <span className="text-primary-custom">₦{Number(bonus).toLocaleString()}</span> {unlocked ? "Referral Reward!" : "next reward"}
          </h3>
        </div>

        {/* Progress */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-slate-700">Referral Progress</span>
            <span className="font-extrabold text-primary-custom" data-testid="success-progress">{current} / {target} friends</span>
          </div>
          <div className="relative h-5 bg-slate-100 rounded-full overflow-hidden flex items-center">
            <div className="h-full bg-primary-custom rounded-full transition-all" style={{ width: `${pct}%` }} />
            <Icon name="emoji_events" className="absolute right-2 text-amber-400 text-lg" />
          </div>
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
            {unlocked ? <>Milestone complete! <Icon name="check" className="text-green-600 text-base" /></> : `${target - current} more to unlock`}
          </p>
        </div>

        {/* Referred friends */}
        {data.referred_friends.length > 0 && (
          <div className="mt-6">
            <p className="font-semibold text-slate-700 mb-3">Referred Friends</p>
            <div className="flex gap-3 flex-wrap">
              {data.referred_friends.slice(0, 8).map((f, i) => (
                <span key={i} className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-sm ring-2 ring-amber-400" style={{ backgroundColor: f.color }} data-testid={`friend-avatar-${i}`}>
                  {f.initials}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Reward credited */}
        {unlocked && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4" data-testid="reward-credited-card">
            <span className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center text-white font-extrabold text-xl">₦</span>
            <div className="flex-1">
              <p className="font-bold text-slate-900">Reward Credited</p>
              <p className="text-sm text-slate-500">₦{Number(bonus).toLocaleString()} added to your wallet</p>
            </div>
            <Icon name="check_circle" className="text-green-500 text-2xl" />
          </div>
        )}

        <button onClick={() => setScreen("invite")} data-testid="refer-more-btn" className="w-full mt-7 bg-primary-custom hover:bg-primary-dark text-white font-bold py-4 rounded-full transition-all">
          Refer More Friends
        </button>
        <button onClick={onOpenWallet} data-testid="view-reward-history" className="w-full mt-3 text-primary-custom font-bold py-2 flex items-center justify-center gap-1">
          View Reward History <Icon name="arrow_forward" className="text-base" />
        </button>
      </div>
    );
  }

  // ---------- INVITE SCREEN ----------
  const m = data.next_milestone;
  return (
    <div className="max-w-lg mx-auto" data-testid="referral-invite-page">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="font-bold text-xl text-slate-900 flex-1 text-center">Invite Friends</h2>
        <button onClick={shareLink} data-testid="invite-share-btn" className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50">
          <Icon name="share" className="text-base" />
        </button>
      </div>

      {/* Hero banner */}
      <div className="relative bg-gradient-to-b from-red-50 to-white rounded-3xl p-6 text-center overflow-hidden">
        <div className="absolute top-4 right-4 bg-red-100 rounded-2xl px-3 py-2 text-right">
          <p className="text-primary-custom font-extrabold leading-none"><span className="text-lg">₦{Number(data.reward_per_referral).toLocaleString()}</span> <span className="text-xs font-semibold">per referral</span></p>
          <p className="text-[10px] text-slate-500">Unlimited earnings</p>
        </div>
        <div className="flex items-center justify-center gap-2 pt-6 pb-2">
          <Icon name="arrow_upward" className="text-primary-custom text-3xl" />
        </div>
        <div className="flex items-center justify-center gap-3">
          <span className="w-12 h-12 rounded-full bg-primary-custom flex items-center justify-center"><Icon name="person" className="text-white" /></span>
          <span className="w-14 h-14 rounded-full bg-primary-custom flex items-center justify-center -mt-3"><Icon name="person" className="text-white text-2xl" /></span>
          <span className="w-12 h-12 rounded-full bg-primary-custom flex items-center justify-center"><Icon name="person" className="text-white" /></span>
        </div>
        <p className="text-slate-700 font-medium mt-4 max-w-xs mx-auto">Earn money when your friends top up with <span className="font-extrabold text-slate-900">TopIt</span>.</p>
      </div>

      {/* Referral code card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5">
        <div className="flex items-center justify-between">
          <p className="text-slate-500 text-sm">Your Referral Code</p>
          <Icon name="redeem" className="text-primary-custom text-xl" />
        </div>
        <p className="text-3xl font-extrabold text-slate-900 tracking-wide mt-2 text-center" data-testid="referral-code">{data.referral_code}</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <CopyButton value={data.referral_code} label="Copy Code" testid="copy-code-btn" className="flex items-center justify-center gap-2 py-3 border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors" />
          <button onClick={regenerate} disabled={regenerating} data-testid="new-code-btn" className="flex items-center justify-center gap-2 py-3 border border-primary-custom text-primary-custom rounded-xl font-semibold hover:bg-primary-custom/5 transition-colors disabled:opacity-60">
            <Icon name={regenerating ? "progress_activity" : "autorenew"} className={`text-base ${regenerating ? "animate-spin" : ""}`} /> New Code
          </button>
        </div>
      </div>

      {/* Referral link row */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 mt-4 flex items-center gap-3">
        <Icon name="link" className="text-primary-custom" />
        <span className="flex-1 text-slate-600 text-sm truncate" data-testid="referral-link">{data.referral_link}</span>
        <CopyButton value={data.referral_link} label="Copy" testid="copy-link-btn" className="flex items-center gap-1 text-primary-custom font-bold text-sm shrink-0" />
      </div>

      {/* Were you referred? Apply a friend's code (only if not already referred) */}
      {data.referred_by ? (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-3 mt-4 flex items-center gap-2" data-testid="referred-by-badge">
          <Icon name="verified" className="text-green-600" />
          <span className="text-sm font-semibold text-green-700">You joined with a friend's referral code</span>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mt-4" data-testid="apply-referral-card">
          <p className="text-sm font-bold text-slate-900">Were you referred by a friend?</p>
          <p className="text-xs text-slate-500 mt-0.5">Enter their code so they get rewarded when you top up.</p>
          <div className="flex gap-2 mt-3">
            <input value={applyCode} onChange={(e) => setApplyCode(e.target.value)} data-testid="apply-referral-input"
              placeholder="e.g. TOPIT-AB123" className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 focus:border-primary-custom outline-none uppercase" />
            <button onClick={applyReferral} disabled={applying || !applyCode.trim()} data-testid="apply-referral-btn"
              className="bg-primary-custom hover:bg-primary-dark text-white font-semibold px-5 rounded-xl disabled:opacity-60">
              {applying ? "..." : "Apply"}
            </button>
          </div>
        </div>
      )}

      {/* How it works */}
      <p className="font-bold text-slate-900 mt-6">How it works</p>
      <div className="grid grid-cols-3 gap-3 mt-3">
        {[
          { n: 1, icon: "person_add", label: "Invite Friend" },
          { n: 2, icon: "smartphone", label: "They Top Up" },
          { n: 3, icon: "account_balance_wallet", label: "You Earn" },
        ].map((s) => (
          <div key={s.n} className="relative bg-slate-50 rounded-2xl p-4 pt-6 text-center">
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-primary-custom text-white text-xs font-bold flex items-center justify-center">{s.n}</span>
            <Icon name={s.icon} className="text-slate-700 text-2xl" />
            <p className="text-xs font-semibold text-slate-700 mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Earnings summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5" data-testid="earnings-summary">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-slate-500 text-sm">Total Earned</p>
            <p className="text-2xl font-extrabold text-slate-900" data-testid="total-earned">₦{Number(data.total_earned).toLocaleString()}</p>
          </div>
          <div className="border-l border-slate-100 pl-4">
            <p className="text-slate-500 text-sm">Friends Joined</p>
            <p className="text-2xl font-extrabold text-slate-900 flex items-center gap-1" data-testid="friends-joined"><Icon name="groups" className="text-primary-custom text-xl" />{data.friends_joined} friends</p>
          </div>
        </div>
        {m && (
          <>
            <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${m.progress_pct}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>Next reward at {m.target} friends</span>
              <span className="font-semibold">{m.progress_pct}%</span>
            </div>
          </>
        )}
        {data.last_unlocked_milestone && (
          <button onClick={() => setScreen("success")} data-testid="view-milestone-btn" className="w-full mt-4 bg-amber-50 border border-amber-200 text-amber-700 font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-amber-100 transition-colors">
            <Icon name="emoji_events" className="text-base" /> You unlocked ₦{Number(data.last_unlocked_milestone.bonus).toLocaleString()} — View reward
          </button>
        )}
      </div>

      {/* Cashback wallet — separate, tangible */}
      {cashback && (
        <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-200 p-5 mt-5" data-testid="cashback-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center"><Icon name="savings" className="text-emerald-600" /></span>
              <div>
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Cashback Wallet</p>
                <p className="text-2xl font-extrabold text-emerald-700" data-testid="cashback-available">₦{Number(cashback.available).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs">Total earned</p>
              <p className="text-slate-700 font-bold" data-testid="cashback-earned">₦{Number(cashback.earned_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <button onClick={transferCashback} disabled={!cashback.can_transfer || transferring} data-testid="cashback-transfer-btn"
            className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
            <Icon name={transferring ? "progress_activity" : "swap_horiz"} className={`text-base ${transferring ? "animate-spin" : ""}`} />
            {transferring ? "Transferring…" : cashback.can_transfer ? "Transfer to main wallet" : "No cashback yet"}
          </button>
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{cashback.conditions}</p>
          {cashback.sources?.length > 0 && (
            <div className="mt-3 border-t border-emerald-100 pt-3 space-y-2" data-testid="cashback-sources">
              <p className="text-xs font-semibold text-slate-600">Recent cashback</p>
              {cashback.sources.slice(0, 4).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 truncate pr-2">{s.description}</span>
                  <span className="text-emerald-600 font-bold shrink-0">+₦{Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Referral pipeline — registered -> qualified -> rewarded */}
      {data.pipeline && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5" data-testid="referral-pipeline">
          <p className="font-bold text-slate-900">Referral Progress</p>
          <p className="text-xs text-slate-500 mt-0.5">Reward pays when a friend funds ₦{Number(data.qualification?.funded_min || 500).toLocaleString()}+ and makes a ₦{Number(data.qualification?.qualify_min || 200).toLocaleString()}+ purchase.</p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { k: "total", label: "Total", testid: "pipe-total" },
              { k: "pending", label: "Pending", testid: "pipe-pending" },
              { k: "qualified", label: "Qualified", testid: "pipe-qualified" },
            ].map((c) => (
              <div key={c.k} className="bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-extrabold text-slate-900" data-testid={c.testid}>{data.pipeline[c.k] ?? 0}</p>
                <p className="text-[11px] text-slate-500">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="text-center"><p className="text-xs text-slate-400">Earned</p><p className="font-bold text-emerald-700" data-testid="pipe-earned">₦{Number(data.pipeline.rewards_earned || 0).toLocaleString()}</p></div>
            <div className="text-center"><p className="text-xs text-slate-400">Paid</p><p className="font-bold text-slate-800" data-testid="pipe-paid">₦{Number(data.pipeline.rewards_paid || 0).toLocaleString()}</p></div>
            <div className="text-center"><p className="text-xs text-slate-400">Outstanding</p><p className="font-bold text-amber-600" data-testid="pipe-outstanding">₦{Number(data.pipeline.outstanding || 0).toLocaleString()}</p></div>
          </div>
          {data.referrals?.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3 space-y-2" data-testid="referral-list">
              {data.referrals.slice(0, 8).map((r) => {
                const badge = {
                  registered: { c: "bg-slate-100 text-slate-600", t: "Registered" },
                  funded: { c: "bg-blue-100 text-blue-700", t: "Funded" },
                  qualified: { c: "bg-indigo-100 text-indigo-700", t: "Qualified" },
                  rewarded: { c: "bg-emerald-100 text-emerald-700", t: "Rewarded" },
                }[r.status] || { c: "bg-slate-100 text-slate-600", t: r.status };
                return (
                  <div key={r.id} className="flex items-center justify-between gap-2" data-testid={`referral-row-${r.id}`}>
                    <span className="text-sm text-slate-700 truncate">{r.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.rewarded && <span className="text-emerald-600 text-xs font-bold">+₦{Number(r.expected_reward).toLocaleString()}</span>}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Share buttons */}
      <div className="space-y-3 mt-6">
        <button onClick={shareWhatsApp} data-testid="share-whatsapp-btn" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-all">
          <Icon name="chat" className="text-xl" /> Share on WhatsApp
        </button>
        <button onClick={shareLink} data-testid="share-link-btn" className="w-full bg-primary-custom hover:bg-primary-dark text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-all">
          <Icon name="ios_share" className="text-xl" /> Share Link
        </button>
        <CopyButton value={data.referral_code} label="Copy Code" testid="share-copy-code-btn" className="w-full border-2 border-primary-custom/40 text-primary-custom font-bold py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-primary-custom/5 transition-all" />
      </div>
    </div>
  );
};

// ============================================================
// CONTACTS — "Select Contact" picker + manual contact list
// ============================================================

